import { useState, type FormEvent } from "react";
import { tEnglish as t, type PhraseKey } from "@buza/i18n";
import { api } from "../api.js";
import { FieldReason, ifMatch, RefusalNotice, toRefusal, type Refusal } from "../zd/refusal.js";
import type { TenantRow } from "../Tenants.js";

// S-042 Z02 status actions: verify (TEN-05), suspend and reactivate (TEN-04). Spec 4.3 Z02 and
// 12.1 Tenant. The buttons follow the 12.1 machine and the 3.3 matrix so a ZD user sees only what
// they may do (ZD support: verify; super administrator: also suspend and reactivate), but the
// server decides and audits every attempt (apps/api/src/zd/tenant-status.ts). The panel uses the
// ZD error pattern (BD-63): the rule's reason, field reasons beside their fields, the stale record
// message with Reload, and entries kept after any refusal.
//
// S-047 adds Move to offboarding (TEN-02, GR-07: from pending verification, active or suspended;
// super administrator only). Screens are paused (4 Nov 2026), so it is the minimal form on the
// S-042 pattern: the agreement end date, which must be today, and nothing else. It moves onto the
// ZD shell with the rest of Z02 later.

export type StatusAction = "verify" | "suspend" | "reactivate" | "offboard";

const ACTIONS: Array<{ action: StatusAction; from: readonly TenantRow["status"][]; superAdminOnly: boolean; label: PhraseKey }> = [
  { action: "verify", from: ["pending_verification"], superAdminOnly: false, label: "console.tenants.status.action_verify" },
  { action: "suspend", from: ["active"], superAdminOnly: true, label: "console.tenants.status.action_suspend" },
  { action: "reactivate", from: ["suspended"], superAdminOnly: true, label: "console.tenants.status.action_reactivate" },
  { action: "offboard", from: ["pending_verification", "active", "suspended"], superAdminOnly: true, label: "console.tenants.offboard.action" },
];

/** The status actions offered on a row. Without a known role every action the state allows is shown. */
export function actionsFor(status: TenantRow["status"], role: "support" | "super_admin" | undefined) {
  return ACTIONS.filter((a) => a.from.includes(status) && (!a.superAdminOnly || role !== "support"));
}

const TITLE: Record<StatusAction, PhraseKey> = {
  verify: "console.tenants.status.verify_title",
  suspend: "console.tenants.status.suspend_title",
  reactivate: "console.tenants.status.reactivate_title",
  offboard: "console.tenants.offboard.title",
};
const INTRO: Record<StatusAction, PhraseKey> = {
  verify: "console.tenants.status.verify_intro",
  suspend: "console.tenants.status.suspend_intro",
  reactivate: "console.tenants.status.reactivate_intro",
  offboard: "console.tenants.offboard.intro",
};
const CONFIRM: Record<StatusAction, PhraseKey> = {
  verify: "console.tenants.status.confirm_verify",
  suspend: "console.tenants.status.confirm_suspend",
  reactivate: "console.tenants.status.confirm_reactivate",
  offboard: "console.tenants.offboard.confirm",
};
const DONE: Record<StatusAction, PhraseKey> = {
  verify: "console.tenants.status.done_verified",
  suspend: "console.tenants.status.done_suspended",
  reactivate: "console.tenants.status.done_reactivated",
  offboard: "console.tenants.offboard.done",
};

export function TenantStatusPanel(props: {
  tenant: TenantRow;
  action: StatusAction;
  onClose: () => void;
  onReload: () => void;
  onDone: (message: string) => void;
}) {
  const { tenant, action } = props;
  const [business, setBusiness] = useState<"" | "address" | "registration">("");
  const [contact, setContact] = useState(false);
  const [terms, setTerms] = useState(false);
  const [operator, setOperator] = useState(false);
  const [reason, setReason] = useState("");
  const [endDate, setEndDate] = useState("");
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const body =
      action === "verify"
        ? { checklist: { business: business || null, contactReachedByPhone: contact, termsAccepted: terms, operatorAgreementAccepted: operator } }
        : action === "offboard"
          ? { agreementEndDate: endDate }
          : { reason: reason.trim() === "" ? null : reason };
    const r = await api<{ tenant: { status: string; version: number } }>(`/v1/zd/tenants/${tenant.id}/actions/${action}`, {
      method: "POST",
      body,
      headers: ifMatch(tenant.version),
    });
    setBusy(false);
    if (r.ok) return props.onDone(`${t(DONE[action])} ${tenant.name}`);
    setRefusal(toRefusal(r));
  }

  const titleId = `z02-${action}-title`;
  return (
    <form onSubmit={submit} noValidate aria-labelledby={titleId} className="z02-status">
      <h3 id={titleId}>{t(TITLE[action])}</h3>
      <p>
        <strong>{tenant.name}</strong>
      </p>
      <p>{t(INTRO[action])}</p>
      <RefusalNotice
        refusal={refusal}
        onReload={() => {
          setRefusal(undefined);
          props.onReload();
          props.onClose();
        }}
      />
      {action === "verify" ? (
        <>
          <fieldset aria-invalid={!!refusal?.fields.business}>
            <legend>{t("console.tenants.status.label_business")}</legend>
            <label>
              <input type="radio" name="business" value="address" checked={business === "address"} onChange={() => setBusiness("address")} />
              {t("console.tenants.status.business_address")}
            </label>
            <label>
              <input
                type="radio"
                name="business"
                value="registration"
                checked={business === "registration"}
                onChange={() => setBusiness("registration")}
              />
              {t("console.tenants.status.business_registration")}
            </label>
            <FieldReason refusal={refusal} field="business" />
          </fieldset>
          <div className="field">
            <label>
              <input type="checkbox" name="contactReachedByPhone" checked={contact} onChange={(e) => setContact(e.target.checked)} />
              {t("console.tenants.status.label_contact")}
            </label>
            <FieldReason refusal={refusal} field="contactReachedByPhone" />
          </div>
          <div className="field">
            <label>
              <input type="checkbox" name="termsAccepted" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
              {t("console.tenants.status.label_terms")}
            </label>
            <FieldReason refusal={refusal} field="termsAccepted" />
          </div>
          <div className="field">
            <label>
              <input type="checkbox" name="operatorAgreementAccepted" checked={operator} onChange={(e) => setOperator(e.target.checked)} />
              {t("console.tenants.status.label_operator")}
            </label>
            <FieldReason refusal={refusal} field="operatorAgreementAccepted" />
          </div>
        </>
      ) : action === "offboard" ? (
        <div className="field">
          <p>{t("console.tenants.offboard.invoices_note")}</p>
          <label>
            {t("console.tenants.offboard.label_date")}
            <input
              type="date"
              name="agreementEndDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              aria-invalid={!!refusal?.fields.agreementEndDate}
              aria-describedby={refusal?.fields.agreementEndDate ? "agreementEndDate-reason" : undefined}
            />
          </label>
          <FieldReason refusal={refusal} field="agreementEndDate" />
        </div>
      ) : (
        <div className="field">
          <label>
            {t(action === "suspend" ? "console.tenants.status.label_reason" : "console.tenants.status.label_note")}
            <textarea
              name="reason"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-invalid={!!refusal?.fields.reason}
              aria-describedby={refusal?.fields.reason ? "reason-reason" : undefined}
            />
          </label>
          <FieldReason refusal={refusal} field="reason" />
        </div>
      )}
      <button type="submit" disabled={busy}>
        {busy ? t("console.tenants.status.saving") : t(CONFIRM[action])}
      </button>
      <button type="button" onClick={props.onClose} disabled={busy}>
        {t("console.tenants.status.close_panel")}
      </button>
    </form>
  );
}
